import { useMutation } from "@tanstack/react-query";

import { dealSectionApi } from "../../../api/client";
import { generateIdempotencyKey } from "../../../lib/idempotencyKey";

export interface AdvancePreApprovalInput {
  approvalId: string;
}

/**
 * Mutation hook backing the pre-approval-advance follow-up call (POST
 * /api/v1/workbench/applications/{approvalId}/deal/pre-approval-advance).
 * Fired fire-and-forget after a successful Workbench save resolves with an
 * exact 200 (see useSaveAllSections) — it never blocks the Save button, its
 * DealStateResponse is intentionally ignored, and a failure here is
 * best-effort/silent: it doesn't affect the save's own success/failure
 * outcome or surface any toast, it's only logged for diagnostics.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useAdvancePreApproval() {
  return useMutation({
    mutationFn: async ({ approvalId }: AdvancePreApprovalInput): Promise<void> => {
      const idempotencyKey = generateIdempotencyKey();
      await dealSectionApi.advancePreApproval(approvalId, idempotencyKey);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.warn("[useAdvancePreApproval] pre-approval-advance call failed", {
        error: error instanceof Error ? error.message : String(error),
      });
    },
  });
}
